import { type ChangeEvent, type FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { FileText, Loader2, Upload, X } from 'lucide-react';

import { toaster } from '@/shared/libs';
import { uploadProviderFiles } from '@entities/Provider';
import type { FileUploadPurpose } from '@psp-aggregator/sdk';

export interface ProviderFileUploadProps {
  accept: string;
  label: string;
  preview?: 'document' | 'image';
  purpose: FileUploadPurpose;
  value?: string;
  onClear?: () => void;
  onUploaded: (urls: string[]) => void;
}

export const ProviderFileUpload: FC<ProviderFileUploadProps> = ({
  accept,
  label,
  onClear,
  onUploaded,
  preview = 'document',
  purpose,
  value,
}) => {
  const { t } = useTranslation();
  const inputId = useId();
  const [uploading, setUploading] = useState(false);

  const handleChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = [...(event.target.files ?? [])];

    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    setUploading(true);

    try {
      onUploaded(await uploadProviderFiles(purpose, files));
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ??
            t('errors.providers.uploadFailed'))
          : t('errors.providers.uploadFailed'),
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <input
        accept={accept}
        className="hidden"
        disabled={uploading}
        id={inputId}
        type="file"
        onChange={(event) => {
          void handleChange(event);
        }}
      />
      {!!value && preview === 'image' && (
        <img
          alt=""
          className="h-14 w-14 shrink-0 rounded-xl border border-border/60 bg-foreground/5 object-cover"
          src={value}
        />
      )}
      {!!value && preview === 'document' && (
        <a
          className="inline-flex max-w-[160px] items-center gap-1.5 truncate text-xs text-indigo-600 hover:underline dark:text-indigo-300"
          href={value}
          rel="noreferrer"
          target="_blank">
          <FileText className="h-3.5 w-3.5 shrink-0" />
          {t('buttons.view')}
        </a>
      )}
      <label
        className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-foreground/5 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-foreground/10"
        htmlFor={inputId}>
        {uploading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Upload className="h-3.5 w-3.5" />
        )}
        {value ? t('buttons.replace') : label}
      </label>
      {!!value && !!onClear && (
        <button
          aria-label={t('buttons.delete')}
          className="cursor-pointer rounded-lg p-1 text-muted-foreground hover:bg-rose-500/10 hover:text-rose-500"
          type="button"
          onClick={onClear}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

ProviderFileUpload.displayName = 'ProviderFileUpload';
